import React, { createContext, useState } from "react";
import { Box, Container, useColorMode } from "@chakra-ui/react";
import { NavbarWithCallToAction } from "./chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "./chakra/FooterWithFourColumns/FooterWithFourColumns";
import { ModuleBreadcrumb } from "./ModuleBreadcrumb";
import { ModuleRouteLayoutOptions } from "../types/ModuleConfig";
import { getColor } from "../brandConfig";

/**
 * Runtime chrome control for pages inside ModulePageLayout. Lets a page hide
 * the breadcrumb bar dynamically (e.g. the calendar's Fit mode reclaims that
 * strip for the grid). Static hiding stays on the route's `noBreadcrumb`.
 */
export const LayoutChromeContext = createContext<{
  setBreadcrumbHidden: (hidden: boolean) => void;
}>({ setBreadcrumbHidden: () => {} });

interface ModulePageLayoutProps {
  moduleConfig: { id: string; name: string; navigation: any[]; [key: string]: any };
  layoutOptions?: ModuleRouteLayoutOptions;
  children: React.ReactNode;
}

/**
 * Wraps a route's page component with Navbar → Breadcrumb → Container → Footer.
 * Used automatically by App.tsx when a route has `useLayout: true`.
 * See vault/how-to/how-to-build-new-module.md.
 */
export const ModulePageLayout: React.FC<ModulePageLayoutProps> = ({
  moduleConfig,
  layoutOptions,
  children,
}) => {
  const { colorMode } = useColorMode();
  const bgMain = getColor("background.main", colorMode);

  const maxW = layoutOptions?.maxW ?? "container.xl";
  const py = layoutOptions?.py ?? 8;
  const noContainer = layoutOptions?.noContainer ?? false;
  const noBreadcrumb = layoutOptions?.noBreadcrumb ?? false;
  const [breadcrumbHidden, setBreadcrumbHidden] = useState(false);

  return (
    <LayoutChromeContext.Provider value={{ setBreadcrumbHidden }}>
      <Box bg={bgMain} minH="100vh" display="flex" flexDirection="column">
        <NavbarWithCallToAction />
        {!noBreadcrumb && !breadcrumbHidden && <ModuleBreadcrumb moduleConfig={moduleConfig as any} />}
        {noContainer ? (
          <Box flex="1">{children}</Box>
        ) : (
          <Container maxW={maxW} px={{ base: 4, md: 8 }} py={py} flex="1">
            {children}
          </Container>
        )}
        <FooterWithFourColumns />
      </Box>
    </LayoutChromeContext.Provider>
  );
};

export default ModulePageLayout;
